import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/auth/AuthProvider";
import { toast } from "sonner";
import type { CircleApplication } from "@/types/circle";

export function useCircleApplication() {
  const { userId } = useAuth();
  const queryClient = useQueryClient();

  const { data: application, isLoading, error } = useQuery({
    queryKey: ["circle-application", userId],
    queryFn: async () => {
      if (!userId) return null;
      
      const { data, error } = await supabase
        .from("circle_applications")
        .select("*")
        .eq("user_id", userId)
        .maybeSingle();

      if (error) throw error;
      return data as CircleApplication | null;
    },
    enabled: !!userId,
  });

  const submitApplication = useMutation({
    mutationFn: async (message?: string) => {
      if (!userId) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("circle_applications")
        .insert({
          user_id: userId,
          message: message || null,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["circle-application", userId] });
      if (data.status === "approved") {
        toast.success("You're in! Your application has been approved.");
      } else {
        toast.success("Application submitted! We'll review it soon.");
      }
    },
    onError: (error) => {
      console.error("Error submitting application:", error);
      toast.error("Failed to submit application. Please try again.");
    },
  });

  return {
    application,
    isLoading,
    error,
    submitApplication,
    status: application?.status || null,
    isPending: application?.status === "pending",
    isApproved: application?.status === "approved",
    isRejected: application?.status === "rejected",
    hasApplied: !!application,
  };
}
